import { Loader2, Workflow } from "lucide-react";
import { PROJECT_ORGANISE_STATE_LABELS, type ProjectOrganise } from "@gb/contracts";
import { Button } from "@/components/ui/button";

/**
 * The files no import has organised yet, and "Organise now" (2026-09-26,
 * tracker RET-04).
 *
 * The project check, the areas, the families and the links ran only when an
 * import was organised. A file uploaded on its own, a file a person kept and a
 * file a step left undone waited for an import that might never come. The
 * worker now organises them within about 30 minutes; this line says how many
 * wait, and lets a person start the run at once.
 *
 * At the top of the Sources tab since 2026-10-03 (the owner): background work
 * a person started must be seen without scrolling. Until then it sat beside
 * the shelves. Not on the "Not organised yet" card either way: the card is
 * itself a button, and a button inside a button is not one a reader can press. Since 2026-09-26 (RET-12) the shelf counts the same
 * gaps: no check, no family decision, no links. "No area" was a fourth until
 * the outline went (2026-10-03, `document-ai/0032`). This line can count
 * a few more: a file of an import still being read, which no list shows until
 * the check has looked at it, and a hidden file whose card changed since.
 *
 * Only while something waits. A project with every file organised shows
 * nothing here.
 */
export function OrganiseLine({
  organise,
  busy,
  error,
  onOrganise,
}: {
  organise: ProjectOrganise;
  /** The request to start the run is on its way. */
  busy: boolean;
  error: string | null;
  onOrganise: () => void;
}) {
  const { files, state } = organise;
  if (files === 0 && (state === "idle" || state === "import")) return null;

  const waiting = files === 1 ? "1 file is not organised yet." : `${files} files are not organised yet.`;
  const said =
    state === "idle" ? waiting : `${files > 0 ? `${waiting} ` : ""}${PROJECT_ORGANISE_STATE_LABELS[state]}`;

  return (
    <section
      aria-label="Organising"
      className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-card-lg border-[1.5px] border-border bg-surface px-5 py-3"
    >
      <Workflow size={16} aria-hidden className="shrink-0 text-muted" />
      <p className="min-w-0 flex-1 text-[0.86rem] text-ink-slate" role="status" aria-live="polite">
        {said}
      </p>
      {state === "idle" && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={onOrganise}>
          {busy && <Loader2 size={14} className="animate-spin" />}
          Organise now
        </Button>
      )}
      {state === "running" && <Loader2 size={14} aria-hidden className="animate-spin text-muted" />}
      {error && (
        <p role="alert" className="w-full text-[0.85rem] font-semibold text-danger">
          {error}
        </p>
      )}
    </section>
  );
}
